import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "space-y-4"
}
const _hoisted_2 = { class: "p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3" }
const _hoisted_3 = { class: "flex items-center gap-2.5 min-w-0" }
const _hoisted_4 = { class: "p-2 rounded-xl bg-secondary/10 dark:bg-secondary-dark/20 text-secondary dark:text-secondary-dark-text flex-shrink-0" }
const _hoisted_5 = { class: "min-w-0" }
const _hoisted_6 = { class: "flex items-center gap-2 flex-wrap" }
const _hoisted_7 = { class: "px-2.5 py-0.5 rounded-full text-xs font-bold bg-secondary/10 text-secondary dark:text-secondary-dark-text border border-secondary/20 capitalize" }
const _hoisted_8 = {
  key: 0,
  class: "px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
}
const _hoisted_9 = { class: "text-xs text-gray-500 dark:text-gray-400 mt-0.5" }
const _hoisted_10 = { class: "flex items-center gap-2" }
const _hoisted_11 = ["href"]
const _hoisted_12 = { class: "rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-100 dark:bg-gray-900 min-h-[420px] flex flex-col" }
const _hoisted_13 = {
  key: 0,
  class: "flex-1 flex flex-col items-center justify-center py-20 space-y-3"
}
const _hoisted_14 = {
  key: 1,
  class: "flex-1 flex flex-col items-center justify-center py-16 px-4 text-center space-y-3"
}
const _hoisted_15 = {
  key: 2,
  class: "w-full h-[600px]"
}
const _hoisted_16 = ["src"]
const _hoisted_17 = {
  key: 3,
  class: "w-full"
}
const _hoisted_18 = {
  key: 4,
  class: "flex-1 flex items-center justify-center p-6 bg-checkerboard"
}
const _hoisted_19 = ["src", "alt"]
const _hoisted_20 = {
  key: 5,
  class: "flex-1 flex flex-col items-center justify-center py-16 px-4 text-center space-y-3"
}
const _hoisted_21 = { class: "w-14 h-14 rounded-2xl bg-gray-200 dark:bg-gray-800 text-gray-500 flex items-center justify-center" }
const _hoisted_22 = { class: "flex items-center justify-between w-full" }

import { ref, computed, watch } from 'vue'
import { BaseModal, BaseButton } from '@/components/common'
import DocxViewer from '@/components/common/DocxViewer.vue'
import type { ApplicationDocument } from '@/types/application'
import { applicationService } from '@/services/applicationService'
import {
  FileText,
  ImageIcon,
  Download,
  ExternalLink,
  AlertCircle,
} from 'lucide-vue-next'

interface Props {
  isVisible: boolean
  document?: ApplicationDocument | null
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ViewApplicationDocumentModal',
  props: {
    isVisible: { type: Boolean },
    document: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const isLoading = ref(false)
const hasError = ref(false)
const previewBlobUrl = ref<string | null>(null)
const previewBlob = ref<Blob | null>(null)

const isPdf = computed(() => {
  const name = props.document?.file_name?.toLowerCase() || ''
  const type = props.document?.file_type?.toLowerCase() || ''
  return name.endsWith('.pdf') || type.includes('pdf')
})

const isImage = computed(() => {
  const name = props.document?.file_name?.toLowerCase() || ''
  const type = props.document?.file_type?.toLowerCase() || ''
  return (
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png') ||
    name.endsWith('.gif') ||
    name.endsWith('.webp') ||
    type.startsWith('image/')
  )
})

const isDocx = computed(() => {
  const name = props.document?.file_name?.toLowerCase() || ''
  const type = props.document?.file_type?.toLowerCase() || ''
  return (
    name.endsWith('.docx') ||
    name.endsWith('.doc') ||
    type.includes('word') ||
    type.includes('officedocument')
  )
})

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

const getCategoryLabel = (category?: string) => {
  switch (category) {
    case 'background_check':
      return 'Background Check Form'
    case 'job_application':
      return 'Job Application Form'
    case 'tax_document':
      return 'Tax Documents'
    case 'certification':
      return 'Certification / License'
    case 'identification':
      return 'Identification / ID Proof'
    case 'resume':
      return 'Resume / CV'
    case 'paper_record':
      return 'Scanned Paper Record'
    case 'general':
    default:
      return category ? category.replace(/_/g, ' ') : 'General Document'
  }
}

const loadPreview = async () => {
  if (!props.document?.document_id) return
  if (!isPdf.value && !isImage.value && !isDocx.value) return

  isLoading.value = true
  hasError.value = false

  try {
    const blob = await applicationService.previewDocumentBlob(props.document.document_id)
    previewBlob.value = blob
    if (previewBlobUrl.value) {
      URL.revokeObjectURL(previewBlobUrl.value)
    }
    previewBlobUrl.value = URL.createObjectURL(blob)
  } catch (err) {
    console.error('Failed to load document preview blob:', err)
    hasError.value = true
  } finally {
    isLoading.value = false
  }
}

const cleanupBlobUrl = () => {
  if (previewBlobUrl.value) {
    URL.revokeObjectURL(previewBlobUrl.value)
    previewBlobUrl.value = null
  }
  previewBlob.value = null
}

watch(
  () => [props.isVisible, props.document?.document_id],
  ([visible, docId]) => {
    if (visible && docId) {
      loadPreview()
    } else {
      cleanupBlobUrl()
    }
  }
)

const closeModal = () => {
  cleanupBlobUrl()
  emit('close')
}

const handleDownload = async () => {
  if (!props.document?.document_id) return
  await applicationService.downloadDocument(props.document.document_id, props.document.file_name)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BaseModal), {
    "is-visible": _ctx.isVisible,
    title: _ctx.document ? _ctx.document.file_name : 'Document Preview',
    subtitle: _ctx.document ? `${getCategoryLabel(_ctx.document.document_category)} • ${formatFileSize(_ctx.document.file_size)}` : '',
    icon: _unref(FileText),
    size: "4xl",
    type: "default",
    "show-footer": true,
    onClose: closeModal
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_22, [
        _createVNode(_unref(BaseButton), {
          variant: "outline",
          size: "sm",
          text: "Close",
          onClick: closeModal
        }),
        (_ctx.document)
          ? (_openBlock(), _createBlock(_unref(BaseButton), {
              key: 0,
              variant: "primary",
              size: "sm",
              text: "Download File",
              icon: _unref(Download),
              onClick: handleDownload
            }, null, 8, ["icon"]))
          : _createCommentVNode("", true)
      ])
    ]),
    default: _withCtx(() => [
      (_ctx.document)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  (isPdf.value)
                    ? (_openBlock(), _createBlock(_unref(FileText), {
                        key: 0,
                        class: "w-5 h-5 text-red-500"
                      }))
                    : (isImage.value)
                      ? (_openBlock(), _createBlock(_unref(ImageIcon), {
                          key: 1,
                          class: "w-5 h-5 text-emerald-500"
                        }))
                      : (_openBlock(), _createBlock(_unref(FileText), {
                          key: 2,
                          class: "w-5 h-5 text-blue-500"
                        }))
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("span", _hoisted_7, _toDisplayString(getCategoryLabel(_ctx.document.document_category)), 1),
                    (_ctx.document.expiry_date)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_8, " Expires: " + _toDisplayString(_ctx.document.expiry_date), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("p", _hoisted_9, _toDisplayString(formatFileSize(_ctx.document.file_size)) + " • Type: " + _toDisplayString(_ctx.document.file_type || 'Unknown'), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_unref(BaseButton), {
                  text: "Download",
                  variant: "outline",
                  size: "sm",
                  icon: _unref(Download),
                  onClick: handleDownload
                }, null, 8, ["icon"]),
                (previewBlobUrl.value)
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 0,
                      href: previewBlobUrl.value,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      class: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-secondary dark:text-secondary-dark-text hover:bg-secondary/10 transition"
                    }, [
                      _createVNode(_unref(ExternalLink), { class: "w-4 h-4" }),
                      _cache[0] || (_cache[0] = _createElementVNode("span", null, "Open in Tab", -1))
                    ], 8, _hoisted_11))
                  : _createCommentVNode("", true)
              ])
            ]),
            _createElementVNode("div", _hoisted_12, [
              (isLoading.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [...(_cache[1] || (_cache[1] = [
                    _createElementVNode("div", { class: "w-8 h-8 border-3 border-secondary border-t-transparent rounded-full animate-spin" }, null, -1),
                    _createElementVNode("p", { class: "text-xs font-medium text-gray-500 dark:text-gray-400" }, "Loading document preview...", -1)
                  ]))]))
                : (hasError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _createVNode(_unref(AlertCircle), { class: "w-10 h-10 text-red-500" }),
                      _cache[2] || (_cache[2] = _createElementVNode("div", null, [
                        _createElementVNode("h4", { class: "text-sm font-bold text-gray-800 dark:text-gray-200" }, "Unable to load inline preview"),
                        _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400 mt-1" }, "Please download the file to view its full contents.")
                      ], -1)),
                      _createVNode(_unref(BaseButton), {
                        text: "Download Document",
                        variant: "primary",
                        size: "sm",
                        icon: _unref(Download),
                        onClick: handleDownload
                      }, null, 8, ["icon"])
                    ]))
                  : (isPdf.value && previewBlobUrl.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                        _createElementVNode("iframe", {
                          src: previewBlobUrl.value,
                          class: "w-full h-full border-0",
                          title: "Application Document PDF Preview"
                        }, null, 8, _hoisted_16)
                      ]))
                    : (isDocx.value && (previewBlobUrl.value || previewBlob.value))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                          _createVNode(DocxViewer, {
                            src: previewBlob.value || previewBlobUrl.value,
                            "file-name": _ctx.document.file_name,
                            "max-height": "600px",
                            "allow-download": false,
                            onDownload: handleDownload
                          }, null, 8, ["src", "file-name"])
                        ]))
                      : (isImage.value && previewBlobUrl.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                            _createElementVNode("img", {
                              src: previewBlobUrl.value,
                              alt: _ctx.document.file_name,
                              class: "max-h-[550px] max-w-full rounded-xl shadow-lg object-contain"
                            }, null, 8, _hoisted_19)
                          ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_20, [
                            _createElementVNode("div", _hoisted_21, [
                              _createVNode(_unref(FileText), { class: "w-7 h-7" })
                            ]),
                            _cache[3] || (_cache[3] = _createElementVNode("div", null, [
                              _createElementVNode("h4", { class: "text-sm font-bold text-gray-800 dark:text-gray-200" }, " Inline preview not supported for this file format "),
                              _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400 mt-1" }, " Spreadsheets and archives can be downloaded directly to your device. ")
                            ], -1)),
                            _createVNode(_unref(BaseButton), {
                              text: "Download File",
                              variant: "primary",
                              size: "sm",
                              icon: _unref(Download),
                              onClick: handleDownload
                            }, null, 8, ["icon"])
                          ]))
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["is-visible", "title", "subtitle", "icon"]))
}
}

})